/**
 * Domain colour rules: status badges, species palette, continuous scales.
 * Brand and surface tokens live in theme.ts. See docs/frontend-design.md §7.
 */

/** Visual tone of a status pill. Maps from the derived statuses in requirements.md P9. */
export type StatusTone = 'pending' | 'active' | 'done' | 'failed' | 'muted';

export const STATUS_COLORS: Record<StatusTone, { color: string; bg: string; border: string }> = {
  pending: { color: '#8a8600', bg: 'rgba(237,233,57,0.18)', border: 'rgba(237,233,57,0.40)' },
  active:  { color: '#2563eb', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.30)' },
  done:    { color: '#16a34a', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.30)' },
  failed:  { color: '#dc2626', bg: 'rgba(220,38,38,0.10)',  border: 'rgba(220,38,38,0.30)' },
  muted:   { color: '#4d6a57', bg: 'rgba(77,106,87,0.10)',  border: 'rgba(77,106,87,0.25)' },
};

/**
 * Tree species by genus, keyed as forun reports them (special classes capitalised). The
 * palette is the annotator's — forestum `src/forestum/vis.py` PARAMS['species.colors'], also
 * used by forestum's QGIS projects — and must stay in sync with it, so a genus looks the same
 * in the annotator, in QGIS and here. Shrub is ours (the annotator has none).
 */
export const SPECIES_COLORS: Record<string, string> = {
  Pinus:       '#2ca02c',
  Betula:      '#cccccc',
  Fagus:       '#ff7f0e',
  Pseudotsuga: '#9467bd',
  Quercus:     '#bcbd22',
  Carpinus:    '#d62728',
  Fraxinus:    '#da77e3',
  Acer:        '#e377c2',
  Abies:       '#1f56b4',
  Ulmus:       '#a11819',
  Tilia:       '#8c564b',
  Larix:       '#17becf',
  Picea:       '#1f77b4',
  Populus:     '#cccccc',
  Alnus:       '#b84896',
  Prunus:      '#cccccc',
  Robinia:     '#ffbb78',
  Salix:       '#98df8a',
  Sorbus:      '#ff9896',
  Snag:        '#cccccc',
  Unknown:     '#cccccc',
  Shrub:       '#98a070',
};

export function speciesColor(genus: string): string {
  return SPECIES_COLORS[genus] ?? SPECIES_COLORS.Unknown;
}

/** Height layers (etáže), upper to lower; the prototype's hues. */
export const STRATA = {
  upper: '#c8a14e',
  middle: '#4eac62',
  lower: '#52b99e',
} as const;

/** Harvest maturity on the map and in the tables. */
export const MATURITY = {
  mature: '#d97706',
  immature: '#8ca08c',
  highlight: '#e03e00',
} as const;

/** Viridis-style ramp for continuous values (height, DBH, volume). t is clamped to [0, 1]. */
export function viridis(t: number): string {
  t = Math.max(0, Math.min(1, t));
  const stops: [number, number, number][] = [
    [68, 1, 84], [59, 82, 139], [33, 145, 140], [94, 201, 98], [253, 231, 37],
  ];
  const seg = t * (stops.length - 1);
  const lo = Math.floor(seg);
  const hi = Math.min(stops.length - 1, lo + 1);
  const f = seg - lo;
  const [r0, g0, b0] = stops[lo]!;
  const [r1, g1, b1] = stops[hi]!;
  const mix = (a: number, b: number) => Math.round(a + f * (b - a));
  return `rgb(${mix(r0, r1)},${mix(g0, g1)},${mix(b0, b1)})`;
}

/**
 * ColorBrewer's RdYlGn, red through yellow to green: the tiler's `rdylgn` colormap (matplotlib's,
 * interpolated between these same eleven colours), which the vegetation indices are drawn in.
 */
export const RDYLGN = [
  '#a50026', '#d73027', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#d9ef8b', '#a6d96a', '#66bd63', '#1a9850', '#006837',
] as const;

/** Colours a chart library needs as literals (Recharts/MapLibre cannot read CSS variables). */
export const CHART = {
  tick:  '#4d6a57',
  minor: '#7fa08c',
  grid:  '#c4d4c9',
  mapBackground: '#e8f0e8',
  /** The "other species" series. */
  other: '#b8c4bc',
  /** Trees outside a highlight on dot maps. */
  dotMuted: '#9aa89f',
} as const;

/** One colour per flight on the altitude chart; cycles past eight. */
export const FLIGHT_COLORS = ['#2563eb', '#d97706', '#2a6e42', '#9333ea', '#dc2626', '#0891b2', '#a16207', '#be185d'];
export const flightColor = (i: number) => FLIGHT_COLORS[i % FLIGHT_COLORS.length]!;

/**
 * Map layer colours. MapLibre paint properties take literals, so the map reads them from
 * here rather than from CSS variables. See docs/frontend-design.md §7.
 */
export const MAP = {
  background: CHART.mapBackground,
  /** Control chrome: white buttons with the brand border, forest icons. */
  controlBg: '#ffffff',
  controlBorder: '#c4d4c9',
  controlIcon: '#2a6e42',
  /** Attribution strip, which sits over the map and must stay readable. */
  attributionBg: 'rgba(255,255,255,0.80)',
  /** Vector layer defaults, by object kind. */
  boundaryLine: '#2a6e42',
  lhpLine: '#a86a2a',
  crownFill: '#8ad4a0',
  crownLine: '#2d6a40',
  treeOutline: '#ffffff',
  /** A mobile scan's areas; orange as in forun's own plots. */
  scanArea: '#e8730c',
  scanAreaFill: 'rgba(232, 115, 12, 0.18)',
  scanAreaSelected: '#9a4a06',
  scanAreaLabelHalo: '#ffffff',
  /** The areas a field crew surveyed (a forest summary's `field_area`); apart from the scan's orange and the plan's brown. */
  surveyArea: '#7c3aed',
  /** A field crew's trunks: a snag ringed dark, a stump hollow in the survey colour. */
  fieldTrunkOutline: '#ffffff',
  fieldSnagRing: '#192b1e',
  fieldStumpRing: '#7c3aed',
  /** A growing stock's units: the plan's brown, as the LHP's own lines. */
  unit: '#a86a2a',
  unitFill: 'rgba(168, 106, 42, 0.06)',
  unitSelected: '#6b3f12',
  unitLabelHalo: '#ffffff',
  /** The veil over the units that are not picked. */
  unitDim: '#ffffff',
  /** An MLS segment that is not a tree. */
  mlsInvalid: '#7a7a72',
  /** A tree whose layer lacks the quantity it is coloured by (a field trunk's volume, a DBH not measured). */
  treeNoValue: '#b3bab4',
  /** The line from a clicked MLS crown to its stem, and the ring round the stem. */
  linkLine: '#192b1e',
  /** Photo points and their clusters. */
  photoPoint: '#2563eb',
  photoOutline: '#ffffff',
  /** The ring of a photo the photogrammetry leaves out for its tilt. */
  photoExcluded: '#dc2626',
  cluster: '#2a6e42',
  clusterText: '#ffffff',
  /** Mission planner (docs/frontend-design.md §7). */
  flightLine: '#1d4ed8',
  missionPhoto: '#2563eb',
  missionWaypoint: '#d97706',
  takeoff: '#dc2626',
  footprintFill: 'rgba(37, 99, 235, 0.15)',
  footprintLine: '#2563eb',
  areaFill: 'rgba(42, 110, 66, 0.12)',
  areaLine: '#2a6e42',
  marginLine: '#4d6a57',
  vertex: '#ffffff',
  vertexOutline: '#2a6e42',
  midpoint: '#c4d4c9',
  /** A boundary polygon the editor flags as invalid. */
  problem: '#dc2626',
  hoverPoint: '#111827',
} as const;

/**
 * Where a photo's camera position comes from (forestum preprocess `positioning.csv`): measured
 * positions green to teal, PPK float amber, a PPK single point brown, positions anchored to measured ones in violet and blue,
 * heights from the take-off in orange and pink, the bare EXIF position red.
 */
export const POSITION_SOURCE = {
  rtk: '#16a34a',
  ppk_fixed: '#0d9488',
  ppk_float: '#ca8a04',
  ppk_single: '#92400e',
  anchored_overlap: '#7c3aed',
  anchored_takeoff: '#2563eb',
  takeoff_point: '#ea580c',
  takeoff_first_photo: '#db2777',
  exif: '#dc2626',
} as const;

/**
 * A crown health screen's categories (forestum `crown_health`): the dead dark red, a snag (the
 * segmentation's class, whatever its spectrum) dark brown, the declining orange-red, the threatened yellow, the healthy green; what the screen could not judge (no
 * pixels, no reference of its genus, outside the orthophoto, skipped as a shrub or too small)
 * in greys, so only the screened crowns catch the eye.
 */
export const CROWN_HEALTH = {
  dead: '#7f1d1d',
  snag: '#57391f',
  declining: '#ea580c',
  threatened: '#eab308',
  healthy: '#16a34a',
  no_reference: '#8b958e',
  no_data: '#a3aca5',
  outside: '#bcc3be',
  skipped: '#d3d8d4',
  /** The outline of a hotspot of dead, snag and declining crowns. */
  hotspot: '#7f1d1d',
} as const;

/**
 * The stress z-score's diverging ramp: far worse than the neighbours dark red, as they are
 * yellow, better green (RdYlGn, its middle at 0).
 */
export const STRESS_STOPS: readonly { value: number; color: string }[] = [
  { value: -6, color: RDYLGN[0] },
  { value: -4.5, color: RDYLGN[1] },
  { value: -3, color: RDYLGN[2] },
  { value: -1.5, color: RDYLGN[4] },
  { value: 0, color: RDYLGN[5] },
  { value: 1, color: RDYLGN[7] },
  { value: 2, color: RDYLGN[9] },
];
